/**
 * Bundle Add-On Polo Upsell — IDEA Uniforms
 * ------------------------------------------------------------
 * Shows a popup modal offering a $10 add-on polo whenever a
 * Fast Bundle bundle is present in the cart.
 *
 * HOW IT WORKS
 * 1. Listens for Shopify's cart update events (and patches fetch
 *    so it also catches AJAX add-to-cart calls from the theme).
 * 2. Checks if any cart line item carries a Fast Bundle discount
 *    titled "BUNDLE" (Fast Bundle explodes bundles into their
 *    component products rather than keeping one bundle line item,
 *    so we detect the discount tag instead of a product handle).
 * 3. If so, and the modal hasn't just been shown/dismissed this
 *    session, it opens a popup with Color + Size dropdowns.
 * 4. Customer picks a variant and clicks "Add polo for $10" ->
 *    we call /cart/add.js to add that exact variant.
 * 5. Multiple polos are allowed (one per bundle, or more if the
 *    customer wants), so the modal can be reopened by re-adding
 *    a bundle or clicking the floating reminder button.
 *
 * EDIT BUNDLE_DISCOUNT_TITLE OR POLO_VARIANTS BELOW IF THESE CHANGE.
 */

(function () {
  "use strict";

  // ---- CONFIG: how we detect a qualifying bundle in the cart ----
  // NOTE: Fast Bundle does NOT add a single line item with the bundle's
  // own handle. It explodes the bundle into its component products, and
  // tags each resulting line with a discount titled "BUNDLE". So instead
  // of matching handles, we check for that discount tag. This works
  // automatically for ANY Fast Bundle bundle, including both of yours.
  var BUNDLE_DISCOUNT_TITLE = "BUNDLE";

  // ---- CONFIG: hidden add-on polo variants, by Color -> Size -> variantId ----
  var POLO_VARIANTS = {
    "Blue Polo": {
      "Youth XS": 41052387016753,
      "Youth S": 41052387049521,
      "Youth M": 41052387082289,
      "Youth L": 41052387115057,
      "Youth XL": 41052387147825,
      "Adult S": 41052387180593,
      "Adult M": 41052387213361,
      "Adult L": 41052387246129,
      "Adult XL": 41052387278897,
      "Adult 2XL": 41052387311665,
      "Adult 3XL": 41052387344433,
      "Adult 4XL": 41052387377201,
      "Adult 5XL": 41052387409969
    },
    "Red Polo": {
      "Youth M": 41052387442737,
      "Youth L": 41052387475505,
      "Youth XL": 41052387508273,
      "Adult S": 41052387541041,
      "Adult M": 41052387573809,
      "Adult L": 41052387606577,
      "Adult XL": 41052387639345,
      "Adult 2XL": 41052387672113,
      "Adult 3XL": 41052387704881,
      "Adult 4XL": 41052387737649,
      "Adult 5XL": 41052387770417
    },
    "Orange Polo": {
      "Youth M": 41052387803185,
      "Youth L": 41052387835953,
      "Youth XL": 41052387868721,
      "Adult S": 41052387901489,
      "Adult M": 41052387934257,
      "Adult L": 41052387967025,
      "Adult XL": 41052387999793,
      "Adult 2XL": 41052388032561,
      "Adult 3XL": 41052388065329,
      "Adult 4XL": 41052388098097,
      "Adult 5XL": 41052388130865
    },
    "Green Polo": {
      "Youth M": 41052388163633,
      "Youth L": 41052388196401,
      "Youth XL": 41052388229169,
      "Adult S": 41052388261937,
      "Adult M": 41052388294705,
      "Adult L": 41052388327473,
      "Adult XL": 41052388360241,
      "Adult 2XL": 41052388393009,
      "Adult 3XL": 41052388425777,
      "Adult 4XL": 41052388458545,
      "Adult 5XL": 41052388491313
    },
    "Black Polo": {
      "Youth M": 41052388524081,
      "Youth L": 41052388556849,
      "Youth XL": 41052388589617,
      "Adult S": 41052388622385,
      "Adult M": 41052388655153,
      "Adult L": 41052388687921,
      "Adult XL": 41052388720689,
      "Adult 2XL": 41052388753457,
      "Adult 3XL": 41052388786225,
      "Adult 4XL": 41052388818993,
      "Adult 5XL": 41052388851761
    }
  };

  // ---- internal state ----
  var modalEl = null;
  var lastDismissedAt = 0;
  var DISMISS_COOLDOWN_MS = 4000; // avoid instant re-open after closing
  var isAdding = false;
  var STORAGE_KEY = "poloUpsellOffersHandled"; // how many polos we've let them add
  var SEEN_QTY_KEY = "poloUpsellSeenBundleQty"; // total qty of BUNDLE-tagged lines last seen

  function getOffersHandled() {
    var val = parseInt(sessionStorage.getItem(STORAGE_KEY) || "0", 10);
    return isNaN(val) ? 0 : val;
  }

  function setOffersHandled(count) {
    try {
      sessionStorage.setItem(STORAGE_KEY, String(count));
    } catch (e) {
      /* sessionStorage unavailable (e.g. private mode) — modal will just
         re-check on next cart change instead of remembering across reloads */
    }
  }

  function bumpOffersHandled() {
    setOffersHandled(getOffersHandled() + 1);
    // Consume one unit of banked allowance now that it's actually been used.
    setAllowance(Math.max(0, getAllowance() - 1));
  }

  function getSeenBundleQty() {
    var val = parseInt(sessionStorage.getItem(SEEN_QTY_KEY) || "0", 10);
    return isNaN(val) ? 0 : val;
  }

  function setSeenBundleQty(qty) {
    try {
      sessionStorage.setItem(SEEN_QTY_KEY, String(qty));
    } catch (e) {
      /* sessionStorage unavailable — falls back to re-detecting each poll */
    }
  }

  var ALLOWANCE_KEY = "poloUpsellAllowance"; // persistent allowance, survives multiple poll ticks
  // until actually consumed by an add — fixes a bug where a one-shot
  // bonus could get silently erased if a poll tick ran before the
  // modal/reminder had a chance to use it.

  function getAllowance() {
    var val = parseInt(sessionStorage.getItem(ALLOWANCE_KEY) || "0", 10);
    return isNaN(val) ? 0 : val;
  }

  function setAllowance(val) {
    try {
      sessionStorage.setItem(ALLOWANCE_KEY, String(val));
    } catch (e) {}
  }

  function buildModal() {
    if (modalEl) return modalEl;

    var overlay = document.createElement("div");
    overlay.id = "polo-upsell-overlay";
    overlay.innerHTML =
      '<div id="polo-upsell-modal" role="dialog" aria-modal="true" aria-labelledby="polo-upsell-title">' +
        '<button type="button" id="polo-upsell-close" aria-label="Close">&times;</button>' +
        '<h2 id="polo-upsell-title">Bundle offer unlocked!</h2>' +
        '<p id="polo-upsell-sub">Add an extra polo for just <strong>$10</strong>.</p>' +
        '<div class="polo-upsell-field">' +
          '<label for="polo-upsell-color">Color</label>' +
          '<select id="polo-upsell-color"></select>' +
        '</div>' +
        '<div class="polo-upsell-field">' +
          '<label for="polo-upsell-size">Size</label>' +
          '<select id="polo-upsell-size"></select>' +
        '</div>' +
        '<button type="button" id="polo-upsell-add">Add polo for $10</button>' +
        '<button type="button" id="polo-upsell-skip">No thanks</button>' +
        '<p id="polo-upsell-status" aria-live="polite"></p>' +
      '</div>';

    document.body.appendChild(overlay);
    modalEl = overlay;

    // populate color dropdown
    var colorSelect = overlay.querySelector("#polo-upsell-color");
    Object.keys(POLO_VARIANTS).forEach(function (color) {
      var opt = document.createElement("option");
      opt.value = color;
      opt.textContent = color;
      colorSelect.appendChild(opt);
    });

    populateSizes(colorSelect.value);

    colorSelect.addEventListener("change", function () {
      populateSizes(colorSelect.value);
    });

    overlay.querySelector("#polo-upsell-close").addEventListener("click", closeModal);
    overlay.querySelector("#polo-upsell-skip").addEventListener("click", closeModal);
    overlay.addEventListener("click", function (e) {
      if (e.target === overlay) closeModal();
    });
    overlay.querySelector("#polo-upsell-add").addEventListener("click", handleAddPolo);

    return overlay;
  }

  function populateSizes(color) {
    var sizeSelect = modalEl.querySelector("#polo-upsell-size");
    sizeSelect.innerHTML = "";
    var sizes = POLO_VARIANTS[color] || {};
    Object.keys(sizes).forEach(function (size) {
      var opt = document.createElement("option");
      opt.value = sizes[size];
      opt.textContent = size;
      sizeSelect.appendChild(opt);
    });
  }

  function openModal() {
    buildModal();
    hideReminderButton();
    modalEl.style.display = "flex";
    document.body.classList.add("polo-upsell-open");
  }

  // ---- Floating "Add another polo" reminder button ----
  // Shown after the modal closes, as long as the customer hasn't reached
  // the cap (one polo per bundle in the cart). Lets them deliberately
  // reopen the offer instead of relying only on the auto-popup, which
  // only fires when a NEW bundle is added.
  var reminderEl = null;

  function buildReminderButton() {
    if (reminderEl) return reminderEl;
    reminderEl = document.createElement("button");
    reminderEl.type = "button";
    reminderEl.id = "polo-upsell-reminder";
    reminderEl.textContent = "Add another polo for $10";
    reminderEl.addEventListener("click", function () {
      openModal();
    });
    document.body.appendChild(reminderEl);
    return reminderEl;
  }

  function hideReminderButton() {
    if (reminderEl) reminderEl.style.display = "none";
  }

  function maybeShowReminderButton() {
    fetch("/cart.js")
      .then(function (res) { return res.json(); })
      .then(function (cart) {
        var bundleCount = countQualifyingBundles(cart);
        var offersHandled = getOffersHandled();
        if (bundleCount > offersHandled) {
          buildReminderButton();
          reminderEl.style.display = "block";
        } else {
          hideReminderButton();
        }
      })
      .catch(function () {});
  }

  function closeModal() {
    if (!modalEl) return;
    modalEl.style.display = "none";
    document.body.classList.remove("polo-upsell-open");
    lastDismissedAt = Date.now();
    maybeShowReminderButton();
  }

  function setStatus(msg, isError) {
    var statusEl = modalEl.querySelector("#polo-upsell-status");
    statusEl.textContent = msg;
    statusEl.style.color = isError ? "#c0392b" : "#2d7a3a";
  }

  function handleAddPolo() {
    if (isAdding) return;
    var sizeSelect = modalEl.querySelector("#polo-upsell-size");
    var variantId = parseInt(sizeSelect.value, 10);
    if (!variantId) {
      setStatus("Please choose a color and size.", true);
      return;
    }

    isAdding = true;
    var addBtn = modalEl.querySelector("#polo-upsell-add");
    addBtn.disabled = true;
    addBtn.textContent = "Adding...";
    setStatus("");

    fetch("/cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] })
    })
      .then(function (res) {
        if (!res.ok) throw new Error("Add to cart failed");
        return res.json();
      })
      .then(function () {
        setStatus("Polo added! Refreshing your cart...", false);
        bumpOffersHandled();
        setTimeout(function () {
          window.location.reload();
        }, 900);
      })
      .catch(function () {
        setStatus("Something went wrong. Please try again.", true);
      })
      .finally(function () {
        isAdding = false;
        addBtn.disabled = false;
        addBtn.textContent = "Add polo for $10";
      });
  }


  // Counts how many separate bundle purchases are in the cart. Fast Bundle
  // tags every component line of a bundle with the same discount key (found
  // in line_level_discount_allocations[].discount_application.key), so we
  // count distinct keys (not line items) to get bundle count — e.g. a
  // 5-item bundle counts as 1 bundle, not 5.
  //
  // IMPORTANT: Shopify's /cart.js does NOT always include
  // line_level_discount_allocations on every item — depending on cart
  // state it can return only the simpler `discounts` array instead
  // (just {amount, title}, no key). So we check both shapes.
  function itemHasBundleDiscount(item) {
    var allocations = item.line_level_discount_allocations || [];
    var hasViaAllocations = allocations.some(function (a) {
      return a.title === BUNDLE_DISCOUNT_TITLE;
    });
    if (hasViaAllocations) return true;

    var simpleDiscounts = item.discounts || [];
    return simpleDiscounts.some(function (d) {
      return d.title === BUNDLE_DISCOUNT_TITLE;
    });
  }

  // Sums the quantity of every BUNDLE-tagged line item. Used as a
  // fallback signal: when Shopify doesn't give us distinct discount keys
  // to count bundles directly, we instead watch this total for INCREASES
  // over time (via sessionStorage) — each increase implies a new bundle
  // was added, since removing/editing a bundle wouldn't increase it.
  function totalBundleTaggedQty(cart) {
    if (!cart || !cart.items) return 0;
    var total = 0;
    cart.items.forEach(function (item) {
      if (itemHasBundleDiscount(item)) total += item.quantity || 0;
    });
    return total;
  }

  // Resolves the best available count of distinct bundles currently in
  // the cart. Prefers exact key-based counting; falls back to inferring
  // from total bundle-tagged quantity when Shopify omits the detailed
  // discount-key data.
  function resolveBundleCount(cart) {
    if (!cart || !cart.items) return 0;
    var seenKeys = {};
    var foundAnyKey = false;
    cart.items.forEach(function (item) {
      var allocations = item.line_level_discount_allocations || [];
      allocations.forEach(function (alloc) {
        if (alloc.title !== BUNDLE_DISCOUNT_TITLE) return;
        var key = alloc.discount_application && alloc.discount_application.key;
        if (key) {
          foundAnyKey = true;
          seenKeys[key] = true;
        }
      });
    });
    if (foundAnyKey) return Object.keys(seenKeys).length;

    // No key data available — infer bundle count from total bundle-tagged
    // quantity relative to what we've tracked so far this session. This
    // doesn't give a true "distinct bundle" count from a cold cart, but
    // it lets us detect increases AND decreases over time, which is what
    // actually matters for granting/revoking polo offer allowance.
    return inferBundleCountFromQty(cart);
  }

  // Keeps a running "inferred bundle count" in sessionStorage, adjusting
  // it up or down as the total bundle-tagged quantity changes. Treats
  // any change in total quantity as +/-1 bundle, which matches the common
  // case of adding/removing one bundle at a time.
  var INFERRED_BUNDLE_COUNT_KEY = "poloUpsellInferredBundleCount";

  function getInferredBundleCount() {
    var val = parseInt(sessionStorage.getItem(INFERRED_BUNDLE_COUNT_KEY) || "0", 10);
    return isNaN(val) ? 0 : val;
  }

  function setInferredBundleCount(val) {
    try {
      sessionStorage.setItem(INFERRED_BUNDLE_COUNT_KEY, String(Math.max(0, val)));
    } catch (e) {}
  }

  function inferBundleCountFromQty(cart) {
    var currentQty = totalBundleTaggedQty(cart);
    var seenQty = getSeenBundleQty();
    var inferredCount = getInferredBundleCount();

    if (currentQty > seenQty) {
      inferredCount += 1;
      setAllowance(getAllowance() + 1);
    } else if (currentQty < seenQty) {
      inferredCount = Math.max(0, inferredCount - 1);
    }

    setSeenBundleQty(currentQty);
    setInferredBundleCount(inferredCount);
    return inferredCount;
  }

  function countQualifyingBundles(cart) {
    var bundleCount = resolveBundleCount(cart);
    reconcileOffersWithBundleCount(bundleCount);
    return getOffersHandled() + getAllowance();
  }

  // If the number of bundles in the cart has dropped below what we've
  // already granted polo offers for, claw back the difference: reduce
  // offersHandled (and any unused allowance) to match, which lets
  // enforcePoloQuantityCap trim the excess polo(s) on the next check.
  function reconcileOffersWithBundleCount(bundleCount) {
    var offersHandled = getOffersHandled();
    if (bundleCount >= offersHandled) return;

    setOffersHandled(bundleCount);
    setAllowance(0); // any banked-but-unused allowance is no longer valid either
  }

  function maybeShowModal() {
    fetch("/cart.js")
      .then(function (res) { return res.json(); })
      .then(function (cart) {
        var bundleCount = countQualifyingBundles(cart);
        var offersHandled = getOffersHandled();
        var cooledDown = Date.now() - lastDismissedAt > DISMISS_COOLDOWN_MS;
        var alreadyOpen = modalEl && modalEl.style.display === "flex";
        if (bundleCount > offersHandled && cooledDown && !alreadyOpen) {
          openModal();
        }
      })
      .catch(function () {});
  }

  // ---- Hook into cart changes ----
  //
  // Fast Bundle renders its own modal (separate from Dawn's add-to-cart
  // flow) and adds items to the cart through its own app bundle (main.min.js),
  // not necessarily via a fetch() call or event we can reliably intercept.
  // Rather than chase Fast Bundle's internals, we poll the cart on a short
  // interval — cheap, robust, and works no matter which app or theme flow
  // changes the cart.

  var POLL_INTERVAL_MS = 2500;

  // ---- Enforce quantity cap on the add-on polo line(s) ----
  // Customers can use the cart page's own "+" stepper to bump the polo's
  // quantity past what they were actually offered. Since Shopify gives us
  // no way to set a max attribute on that input, we instead watch the
  // cart and correct the quantity back down via /cart/change.js if it
  // ever exceeds the number of offers we've let them accept.
  var ALL_POLO_VARIANT_IDS = (function () {
    var ids = {};
    Object.keys(POLO_VARIANTS).forEach(function (color) {
      var sizes = POLO_VARIANTS[color];
      Object.keys(sizes).forEach(function (size) {
        ids[sizes[size]] = true;
      });
    });
    return ids;
  })();

  var isCorrectingQty = false;
  var LINE_QTY_SNAPSHOT_KEY = "poloUpsellLineQtySnapshot";

  function getLineQtySnapshot() {
    try {
      var raw = sessionStorage.getItem(LINE_QTY_SNAPSHOT_KEY);
      return raw ? JSON.parse(raw) : {};
    } catch (e) {
      return {};
    }
  }

  function setLineQtySnapshot(snapshot) {
    try {
      sessionStorage.setItem(LINE_QTY_SNAPSHOT_KEY, JSON.stringify(snapshot));
    } catch (e) {}
  }

  function enforcePoloQuantityCap(cart) {
    if (isCorrectingQty || !cart || !cart.items) return;

    var poloLines = cart.items.filter(function (item) {
      return !!ALL_POLO_VARIANT_IDS[item.variant_id];
    });

    var totalPoloQty = 0;
    poloLines.forEach(function (item) {
      totalPoloQty += item.quantity || 0;
    });

    var allowedQty = getOffersHandled();
    var snapshot = getLineQtySnapshot();

    if (totalPoloQty <= allowedQty) {
      // Within cap — just keep the snapshot in sync so future increases
      // are measured from the correct baseline.
      var freshSnapshot = {};
      poloLines.forEach(function (item) {
        freshSnapshot[item.key] = item.quantity;
      });
      setLineQtySnapshot(freshSnapshot);
      return;
    }

    // Over the cap — find the SPECIFIC line whose quantity increased
    // since our last snapshot (rather than guessing by array position,
    // which doesn't reliably reflect "most recently edited" and could
    // target — and zero out — the wrong polo line entirely).
    var increasedLine = null;
    var increasedBy = 0;
    poloLines.forEach(function (item) {
      var previousQty = snapshot[item.key] || 0;
      var delta = item.quantity - previousQty;
      if (delta > increasedBy) {
        increasedBy = delta;
        increasedLine = item;
      }
    });

    // Fallback: if we have no usable snapshot yet (e.g. first run this
    // session), fall back to the single line with the highest quantity,
    // which is the most likely candidate for "just bumped".
    if (!increasedLine) {
      poloLines.forEach(function (item) {
        if (!increasedLine || item.quantity > increasedLine.quantity) {
          increasedLine = item;
        }
      });
    }

    if (!increasedLine) return;

    var excess = totalPoloQty - allowedQty;
    var newQty = Math.max(0, increasedLine.quantity - excess);

    isCorrectingQty = true;
    fetch("/cart/change.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ id: increasedLine.key, quantity: newQty })
    })
      .then(function () {
        showQtyCapNotice();
        window.location.reload();
      })
      .catch(function () {})
      .finally(function () {
        isCorrectingQty = false;
      });
  }

  function showQtyCapNotice() {
    try {
      sessionStorage.setItem("poloUpsellQtyCapNotice", "1");
    } catch (e) {}
  }

  function maybeShowQtyCapNotice() {
    var shouldShow = false;
    try {
      shouldShow = sessionStorage.getItem("poloUpsellQtyCapNotice") === "1";
      sessionStorage.removeItem("poloUpsellQtyCapNotice");
    } catch (e) {}
    if (!shouldShow) return;

    var notice = document.createElement("div");
    notice.id = "polo-upsell-qty-notice";
    notice.textContent = "We've adjusted your polo quantity — the $10 price only applies to one polo per bundle.";
    document.body.appendChild(notice);
    setTimeout(function () {
      if (notice.parentNode) notice.parentNode.removeChild(notice);
    }, 6000);
  }

  function pollCart() {
    fetch("/cart.js")
      .then(function (res) { return res.json(); })
      .then(function (cart) {
        // Resolve bundle count FIRST so offersHandled is reconciled
        // (clawed back if a bundle was removed) before we enforce the
        // polo quantity cap against it — otherwise the cap check would
        // use a stale, too-high allowance for one extra poll cycle.
        var bundleCount = countQualifyingBundles(cart);
        enforcePoloQuantityCap(cart);

        var offersHandled = getOffersHandled();
        var cooledDown = Date.now() - lastDismissedAt > DISMISS_COOLDOWN_MS;
        var alreadyOpen = modalEl && modalEl.style.display === "flex";

        if (bundleCount > offersHandled && cooledDown && !alreadyOpen) {
          openModal();
        } else if (!alreadyOpen) {
          // Keep the floating reminder button in sync even when we're not
          // popping the modal open (e.g. cap already reached, or still
          // cooling down after a recent dismiss).
          if (bundleCount > offersHandled) {
            buildReminderButton();
            reminderEl.style.display = "block";
          } else {
            hideReminderButton();
          }
        }
      })
      .catch(function () {});
  }

  // Also keep the event-based triggers as a fast-path for normal theme
  // add-to-cart actions (cheaper/quicker than waiting for the next poll).
  var originalFetch = window.fetch;
  window.fetch = function () {
    var args = arguments;
    var url = typeof args[0] === "string" ? args[0] : (args[0] && args[0].url) || "";
    var result = originalFetch.apply(this, args);
    if (url.indexOf("/cart/add") !== -1) {
      result.then(function () {
        setTimeout(maybeShowModal, 300);
      });
    }
    return result;
  };

  document.addEventListener("cart:updated", function () {
    setTimeout(maybeShowModal, 300);
  });
  document.addEventListener("cart:refresh", function () {
    setTimeout(maybeShowModal, 300);
  });

  // Start polling once the page is ready. Runs continuously at a low
  // frequency so it catches Fast Bundle's own add-to-cart flow.
  //
  // IMPORTANT: this script loads with `defer`, which can mean
  // DOMContentLoaded has ALREADY fired by the time this code runs on a
  // fast-loading page. If we only ever listen for that event, the
  // listener would never fire and polling would silently never start.
  // So we check document.readyState first and start immediately if the
  // page is already loaded, falling back to the event listener only if
  // the page is still loading.
  function startPolling() {
    maybeShowQtyCapNotice();
    pollCart();
    setInterval(pollCart, POLL_INTERVAL_MS);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", startPolling);
  } else {
    // readyState is already "interactive" or "complete" — start now.
    startPolling();
  }
})();